import api from '../api/axios';
import { firebaseAuthService } from './firebaseAuthService';
import { LoginCredentials, LoginResponse, AuthTokens, ForgotPasswordRequest, ForgotPasswordResponse, VerifyOtpRequest, VerifyOtpResponse, RecoverPasswordRequest } from './types';

const AUTH_ENDPOINTS = {
  LOGIN: '/api/shop/login',
  LOGOUT: '/api/shop/logout',
  REFRESH: '/api/shop/refresh-token',
  REGISTER: '/api/shop/register',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  VERIFY_OTP: '/api/auth/verify-otp',
  RECOVER_PASSWORD: '/api/auth/recover-password',
};

export const authService = {
  async register(userData: any): Promise<LoginResponse> {
    try {
      // Try to create Firebase user first, but ignore errors
      const { email, password } = userData;
      try {
        await firebaseAuthService.createUser(email, password);
        console.log('Firebase user created successfully');
      } catch (firebaseError) {
        console.warn('Firebase user creation failed, proceeding with backend registration:', firebaseError);
        // Continue with backend registration even if Firebase fails
      }
      
      // Always proceed with backend API registration
      const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.REGISTER, userData);
      return response.data;
    } catch (error) {
      console.error('Backend registration error:', error);
      throw error;
    }
  },

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post(AUTH_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  async refreshToken(): Promise<AuthTokens> {
    const tokens = localStorage.getItem('auth_tokens');
    if (!tokens) {
      throw new Error('No refresh token available');
    }

    const { refresh_token } = JSON.parse(tokens);
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<{ auth: AuthTokens }>(AUTH_ENDPOINTS.REFRESH, {
      refresh_token,
    });
    
    return response.data.auth;
  },

  setAuthToken(token: string): void {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  },

  removeAuthToken(): void {
    delete api.defaults.headers.common['Authorization'];
  },

  isTokenExpired(expiresAt: number): boolean {
    // Add 5 minute buffer to refresh token before it actually expires
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return Date.now() >= (expiresAt * 1000) - bufferTime;
  },

  getStoredTokens(): AuthTokens | null {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      return tokens ? JSON.parse(tokens) : null;
    } catch (error) {
      console.error('Error parsing stored tokens:', error);
      return null;
    }
  },

  updateStoredTokens(tokens: AuthTokens): void {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  },

  async forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await api.post<ForgotPasswordResponse>(AUTH_ENDPOINTS.FORGOT_PASSWORD, request);
    return response.data;
  },

  async verifyOtp(request: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    const response = await api.post<VerifyOtpResponse>(AUTH_ENDPOINTS.VERIFY_OTP, request);
    return response.data;
  },

  async recoverPassword(request: RecoverPasswordRequest): Promise<{ message: string; success: boolean }> {
    const response = await api.post<{ message: string; success: boolean }>(AUTH_ENDPOINTS.RECOVER_PASSWORD, request);
    return response.data;
  },
}; 